import React, { useState, useEffect } from 'react';
import { Card, Typography, Row, Col, Progress, Button, Space, message, Spin, Empty, Modal, Tag, App } from 'antd';
import { LeftOutlined, RightOutlined, CheckOutlined, BookOutlined } from '@ant-design/icons';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { examApi } from '../../utils/api';
import styles from './index.module.css';
import axios from 'axios';
import api from '../../utils/api';

const { Title, Text } = Typography;

const QUESTION_TYPE_MAP = {
  'single': '单选题',
  'multiple': '多选题',
  'judge': '判断题',
  'essay': '问答题',
  'calculation': '计算题',
  '单选题': '单选题',
  '多选题': '多选题',
  '判断题': '判断题',
  '问答题': '问答题',
  '计算题': '计算题',
  // 添加其他可能的映射
  'single-choice': '单选题',
  'multiple-choice': '多选题',
  'true-false': '判断题',
  'short-answer': '问答题',
  'calculation-problem': '计算题',
  'Indefinite': '不定项选择',
  'case': '案例分析题',
};

const getDefaultCollection = (category) => {
  switch(category) {
    case 'teacher': return 'teacher_exam';
    case 'economics': return 'economist_exam';
    case 'accounting': return 'accounting_exam';
    default: return 'economist_exam';
  }
};

const getCategoryCollection = async (category) => {
  try {
    const response = await axios.get(`/api/categories/${category}`);
    if (response.data?.success) {
      return response.data.category.collection || getDefaultCollection(category);
    }
    throw new Error('获取分类信息失败');
  } catch (error) {
    console.error('获取分类集合失败:', error);
    return getDefaultCollection(category); // 使用新的默认集合逻辑
  }
};

const QuestionTypeTag = ({ type }) => {
  const displayType = QUESTION_TYPE_MAP[type] || QUESTION_TYPE_MAP['single'];

  return (
    <Tag color="#1ED6FF" className={styles.questionType}>
      {displayType}
    </Tag>
  );
};

// 添加答案显示组件
const AnswerDisplay = ({ answer }) => {
  // 处理答案为数组的情况
  if (Array.isArray(answer)) {
    return (
      <div className={styles.answerList}>
        {answer.map((ans, index) => (
          <Tag
            key={index}
            className={styles.answerTag}
            color="#52c41a"
          >
            {ans}
          </Tag>
        ))}
      </div>
    );
  }

  // 处理答案为字符串的情况
  if (typeof answer === 'string') {
    return (
      <Tag
        className={styles.answerTag}
        color="#52c41a"
      >
        {answer}
      </Tag>
    );
  }

  // 处理其他情况
  return <span className={styles.answerText}>{JSON.stringify(answer)}</span>;
};

const ExamPractice = () => {
  const { category, examData } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // 解析 URL 路径，提取真正的集合名称和分类
  const [collectionName, setCollectionName] = useState(() => {
    // 从 URL 路径中提取集合名称
    const pathParts = location.pathname.split('/');
    // 路径格式应该是 /exam/practice/collection_name/exam_data
    if (pathParts.length >= 4) {
      return pathParts[3]; // 这是集合名称
    }
    return category || getDefaultCollection(category);
  });

  // 尝试从 URL 参数中提取考试数据
  const decodedExamData = examData ? decodeURIComponent(examData) : '';

  // 尝试从location.state获取信息，如果不存在则构建默认值
  const examInfo = location.state?.examInfo || {
    exam_data: decodedExamData,
    category: location.state?.category || '', // 初始为空
    title: decodedExamData
  };

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [mode, setMode] = useState('review');
  const [showResult, setShowResult] = useState(false);
  const [categoryName, setCategoryName] = useState(examInfo.category || '考试练习');
  const [statistics, setStatistics] = useState({
    correct: 0,
    incorrect: 0,
    blank: 0
  });
  const [error, setError] = useState(null);
  const { modal } = App.useApp();

  // Get examCategory from URL search params
  const searchParams = new URLSearchParams(location.search);
  const examCategory = searchParams.get('examCategory');

  useEffect(() => {
    // 直接获取试题，不尝试获取分类信息
    fetchQuestions();
    window.scrollTo(0, 0);
  }, [collectionName, decodedExamData]); // 依赖于解析后的参数

  const fetchQuestions = async () => {
    try {
      setLoading(true);

      // 打印检查路由状态和构建的参数
      console.log('URL Parameters:', { collectionName, examData: decodedExamData });

      // 构建 API 请求参数 - 不传递 category 参数，让后端处理
      const requestParams = {
        exam_data: decodedExamData
      };

      // 如果有分类信息，添加到请求参数中
      if (location.state?.examInfo?.category) {
        requestParams.category = location.state.examInfo.category;
      }

      console.log('API Request:', `/exam/${collectionName}`, requestParams);

      const response = await api.get(`/exam/${collectionName}`, {
        params: requestParams
      });

      if (response.data.success) {
        // 如果响应中包含分类名称，更新显示
        if (response.data.data?.categoryName) {
          setCategoryName(response.data.data.categoryName);
        }

        const formattedQuestions = response.data.data.questions.map(q => {
          // 打印原始题目数据
          console.log('Raw question data:', q);
          return {
            ...q,  // 保留原始题目的所有字段
            _id: q._id,
            type: normalizeQuestionType(q.type),
            questionText: q.question || q.questionText,
            options: q.options,
            answer: Array.isArray(q.answer) ? q.answer : [q.answer],
            explanation: q.explanation || '',
            // 添加这些字段
            category: q.category || '',
            exam_data: q.exam_data || decodedExamData
          };
        });

        // 打印格式化后的题目
        console.log('Formatted questions:', formattedQuestions);
        setQuestions(formattedQuestions);
      } else {
        throw new Error(response.data.message || '获取题目失败');
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      message.error('获取题目失败: ' + (error.message || '未知错误'));
      setError(error.message || '获取题目失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const currentQuestion = questions[currentIndex];

  const handleOptionClick = (optionLabel) => {
    if (currentQuestion?.type === 'multiple') {
      setAnswers(prev => {
        const currentAnswers = prev[currentIndex] || [];
        const newAnswers = currentAnswers.includes(optionLabel)
          ? currentAnswers.filter(label => label !== optionLabel)
          : [...currentAnswers, optionLabel];

        return {
          ...prev,
          [currentIndex]: newAnswers
        };
      });
    } else {
      setAnswers(prev => ({
        ...prev,
        [currentIndex]: [optionLabel]
      }));
    }
  };

  const handleSubmit = () => {
    modal.confirm({
      title: '确认提交',
      content: '提交后将无法继续修改答案，是否确认提交？',
      okText: '确认提交',
      cancelText: '继续答题',
      onOk: async () => {
        try {
          let correctCount = 0;
          let incorrectCount = 0;
          let blankCount = 0;
          const wrongQuestions = []; // 收集所有错题，一次性提交

          // 第一步：计算统计数据并收集错题
          for (let i = 0; i < questions.length; i++) {
            const question = questions[i];
            const userAnswer = answers[i] || [];
            const correctAnswer = question.answer;

            if (userAnswer.length === 0) {
              blankCount++;
            } else {
              const isCorrect = userAnswer.length === correctAnswer.length &&
                userAnswer.every(ans => correctAnswer.includes(ans));

              if (isCorrect) {
                correctCount++;
              } else {
                incorrectCount++;
                // 收集错题数据
                wrongQuestions.push({
                  questionId: question._id || question.id,
                  examData: question.exam_data,
                  category: location.state?.examInfo?.category || question.category || '',
                  collection: collectionName,
                  questionData: question,
                  userAnswer: userAnswer // 记录用户的错误答案
                });
              }
            }
          }

          // 更新统计数据
          setStatistics({
            correct: correctCount,
            incorrect: incorrectCount,
            blank: blankCount
          });

          // 计算正确率
          const correctRate = (correctCount/questions.length*100).toFixed(1);

          // 第二步：并行处理 - 提交错题和更新用户正确率
          const promises = [];

          // 提交错题（如果有）
          if (wrongQuestions.length > 0) {
            console.log(`准备提交 ${wrongQuestions.length} 道错题`);

            // 逐个提交错题
            for (const wrongQuestion of wrongQuestions) {
              promises.push(
                api.post('/study/wrong-questions', wrongQuestion)
                  .catch(error => {
                    console.error('提交错题失败:', error, wrongQuestion);
                    // 不阻止整体流程，只记录错误
                    return { success: false, error };
                  })
              );
            }
          }

          // 更新用户正确率
          console.log('更新用户正确率:', {
            correctCount,
            totalQuestions: questions.length,
            correctRate
          });


          const query = new URLSearchParams(location.search);
          const examCategory = query.get('examCategory');
          promises.push(
            api.post('/exam/submit-answer', {
              correctCount,
              totalQuestions: questions.length,
              examId: examData,
              examInfo: {
                collection: collectionName
              },
              examCategory: examCategory || ''
            }).catch(error => {
              console.error('更新用户正确率失败:', error);
              // 不阻止整体流程，只记录错误
              return { success: false, error };
            })
          );

          // 等待所有请求完成
          const results = await Promise.allSettled(promises);

          // 检查结果
          const failedRequests = results.filter(r => r.status === 'rejected' || (r.value && !r.value.success));

          // 显示结果
          setShowResult(true);

          // 统一使用 info 类型的弹窗，内容保持一致
          if (failedRequests.length > 0) {
            console.warn('部分请求失败:', failedRequests);
          } else {
            console.log('所有数据提交成功');
          }

          // 使用 React 的 Fragment 和 <br/> 来确保正确换行
          modal.info({
            title: '提交结果',
            content: (
              <div>
                得分：{correctCount}分，正确率：{correctRate}%
                <br />
                请到学习中心查看统计报告
              </div>
            ),
            okText: '我知道了'
          });
        } catch (error) {
          console.error('提交答案过程中发生错误:', error);
          // 使用弹窗替代消息提示
          modal.error({
            title: '提交失败',
            content: (
              <div>
                提交答案失败: {error.message || '未知错误'}
                <br />
                请到学习中心查看统计报告
              </div>
            ),
            okText: '我知道了'
          });
          // 即使出错，也允许用户查看结果
          setShowResult(true);
        }
      }
    });
  };

  // 题型标准化函数
  const normalizeQuestionType = (type) => {
    // 处理空值或未定义
    if (!type) return 'single';

    // 统一转换为小写处理
    const lowerType = type.toLowerCase();

    // 映射关系
    const typeMap = {
      // 中文映射
      '单选': 'single',
      '单选题': 'single',
      '多选': 'multiple',
      '多选题': 'multiple',
      '判断': 'judge',
      '判断题': 'judge',
      '问答': 'essay',
      '问答题': 'essay',
      '计算': 'calculation',
      '计算题': 'calculation',

      // 英文映射
      'single': 'single',
      'single-choice': 'single',
      'multiple': 'multiple',
      'multiple-choice': 'multiple',
      'judge': 'judge',
      'true-false': 'judge',
      'essay': 'essay',
      'short-answer': 'essay',
      'calculation': 'calculation',
      'calculation-problem': 'calculation'
    };

    // 尝试直接匹配
    if (typeMap[type]) {
      return typeMap[type];
    }

    // 尝试小写匹配
    if (typeMap[lowerType]) {
      return typeMap[lowerType];
    }

    // 如果都没匹配上，返回默认值
    console.warn(`未知的题型: ${type}，使用默认题型: single`);
    return 'single';
  };

  return (
    <div className={styles.practiceContainer}>
      <div className={styles.titleBar}>
        <div className={styles.titleContent}>
          <Button
            icon={<LeftOutlined />}
            onClick={() => navigate(-1)}
            className={styles.backButton}
          >
            返回
          </Button>
          <Title level={3} className={styles.pageTitle}>
            {categoryName || '考试练习'} - {decodedExamData}
          </Title>
          
          {/* Add Knowledge Notes button */}
          <Button
            icon={<BookOutlined />}
            onClick={() => window.open(`/study-book/${examCategory || 'eco-01'}`, '_blank')}
          >
            知识笔记
          </Button>
          
          <div className={styles.modeButtons}>
            <Button
              type={mode === 'review' ? 'primary' : 'default'}
              onClick={() => setMode('review')}
              className={styles.modeButton}
            >
              复习模式
            </Button>
            <Button
              type={mode === 'exam' ? 'primary' : 'default'}
              onClick={() => setMode('exam')}
              className={styles.modeButton}
            >
              考试模式
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.questionSection}>
          <Card className={styles.questionCard}>
            <div className={styles.header}>
              <div className={styles.questionType}>
                {QUESTION_TYPE_MAP[currentQuestion?.type]}
                {/*{currentQuestion?.type === 'multiple' ? (*/}
                {/*  <span className={styles.typeTag}>多选题</span>*/}
                {/*) : (*/}
                {/*  <span className={styles.typeTag}>单选题</span>*/}
                {/*)}*/}
              </div>
              {questions.length > 0 && (
                <Text type="secondary">
                  第 {currentIndex + 1} 题 / 共 {questions.length} 题
                </Text>
              )}
            </div>

            {loading ? (
              <div className={styles.loadingContainer}>
                <Spin size="large" tip="加载试题中..." />
              </div>
            ) : error ? (
              <div className={styles.errorContainer}>
                <Empty
                  description={
                    <span>
                      加载失败: {error}
                      <br />
                      <Button
                        type="primary"
                        onClick={fetchQuestions}
                        style={{ marginTop: '16px' }}
                      >
                        重试
                      </Button>
                    </span>
                  }
                />
              </div>
            ) : questions.length > 0 ? (
              currentQuestion ? (
                <div className={styles.questionContent}>
                  <div className={styles.questionText}>
                    {currentQuestion.questionText}
                  </div>

                  <div className={styles.options}>
                    {currentQuestion.options?.map((option) => (
                      <div
                        key={option.label}
                        className={`${styles.option} ${
                          answers[currentIndex]?.includes(option.label) ? styles.optionSelected : ''
                        }`}
                        onClick={() => handleOptionClick(option.label)}
                      >
                        <div className={styles.optionContent}>
                          <Text strong>{option.label}.</Text> {option.text}
                        </div>
                        {answers[currentIndex]?.includes(option.label) && (
                          <CheckOutlined className={styles.checkIcon} />
                        )}
                      </div>
                    ))}
                  </div>

                  {(mode === 'review' || (mode === 'exam' && showResult)) && (
                    <div className={styles.answerExplanation}>
                      <div className={styles.answerSection}>
                        <Title level={5}>正确答案</Title>
                        <AnswerDisplay answer={currentQuestion.answer} />
                      </div>
                      <div className={styles.explanationSection}>
                        <Title level={5}>解析</Title>
                        <div className={styles.explanation}>
                          {currentQuestion.explanation || '暂无解析'}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.noQuestion}>
                  <Empty description={<span>该分类下暂无试题</span>} />
                </div>
              )
            ) : (
              <div className={styles.noQuestion}>
                <Empty description={<span>该分类下暂无试题</span>} />
              </div>
            )}

            <div className={styles.navigationButtons}>
              {mode === 'exam' ? (
                showResult ? (
                  <Space size="large">
                    <Button
                      size="large"
                      type="primary"
                      onClick={() => setMode('review')}
                    >
                      查看解析
                    </Button>
                  </Space>
                ) : (
                  <Space size="large" className={styles.examNavigation}>
                    <div>
                      <Button
                        size="large"
                        icon={<LeftOutlined />}
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}
                      >
                        上一题
                      </Button>
                      <Button
                        size="large"
                        icon={<RightOutlined />}
                        onClick={handleNext}
                        disabled={currentIndex === questions.length - 1}
                        style={{ marginLeft: '16px' }}
                      >
                        下一题
                      </Button>
                    </div>
                    <Button
                      size="large"
                      type="primary"
                      onClick={handleSubmit}
                    >
                      提交答案
                    </Button>
                  </Space>
                )
              ) : (
                <Space size="large">
                  <Button
                    size="large"
                    icon={<LeftOutlined />}
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}
                  >
                    上一题
                  </Button>
                  <Button
                    size="large"
                    type="primary"
                    icon={<RightOutlined />}
                    onClick={handleNext}
                    disabled={currentIndex === questions.length - 1}
                  >
                    下一题
                  </Button>
                </Space>
              )}
            </div>
          </Card>
        </div>

        <div className={styles.answerCardSection}>
          <Card className={styles.answerCard}>
            <Title level={4} className={styles.answerCardTitle}>答题卡</Title>
            <div className={styles.answerGrid}>
              {questions.map((question, index) => {
                let buttonClass = styles.answerButton;
                if (currentIndex === index) {
                  buttonClass += ` ${styles.currentQuestion}`;
                }

                if (mode === 'exam' && showResult) {
                  const userAnswer = answers[index] || [];
                  const correctAnswer = question.answer;

                  if (userAnswer.length === 0) {
                    buttonClass += ` ${styles.blankAnswer}`;
                  } else if (
                    userAnswer.length === correctAnswer.length &&
                    userAnswer.every(ans => correctAnswer.includes(ans))
                  ) {
                    buttonClass += ` ${styles.correctAnswer}`;
                  } else {
                    buttonClass += ` ${styles.incorrectAnswer}`;
                  }
                } else if (answers[index]) {
                  buttonClass += ` ${styles.answeredQuestion}`;
                }

                return (
                  <Button
                    key={index}
                    className={buttonClass}
                    onClick={() => setCurrentIndex(index)}
                  >
                    {index + 1}
                  </Button>
                );
              })}
            </div>
            <div className={styles.answerCardFooter}>
              <div className={styles.answerStatus}>
                {mode === 'exam' && showResult ? (
                  <>
                    <Title level={4} className={styles.statisticsTitle}>答题统计</Title>
                    <div className={styles.statisticsContent}>
                      <div className={styles.statusItem}>
                        <div className={styles.statusDot} style={{ background: '#52c41a' }} />
                        <span className={styles.statusText}>答对题 ({statistics.correct})</span>
                      </div>
                      <div className={styles.statusItem}>
                        <div className={styles.statusDot} style={{ background: '#ff4d4f' }} />
                        <span className={styles.statusText}>答错题 ({statistics.incorrect})</span>
                      </div>
                      <div className={styles.statusItem}>
                        <div className={styles.statusDot} style={{ background: '#ffffff3d' }} />
                        <span className={styles.statusText}>未答题 ({statistics.blank})</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className={styles.statusItem}>
                      <div className={styles.statusDot} style={{ background: '#1ED6FF' }} />
                      <span>已答题 ({Object.keys(answers).length})</span>
                    </div>
                    <div className={styles.statusItem}>
                      <div className={styles.statusDot} style={{ background: '#ffffff3d' }} />
                      <span>未答题 ({questions.length - Object.keys(answers).length})</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const ExamPracticeWrapper = () => (
  <App>
    <ExamPractice />
  </App>
);

export default ExamPracticeWrapper;